import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext({ user: null });

const NOTICE_KEY = "vl-auth-notice";
let kicked = false;
export const apiFetch = async (path, opts = {}) => {
  // FormData bodies must let the browser set the multipart boundary; a forced JSON header makes FastAPI reject every field as missing.
  const base = opts.body instanceof FormData ? {} : { "Content-Type": "application/json" };
  // agency = B2B portal pages, where demo accounts act as the agency they picked (also for /api/portal case calls made on a traveller's behalf).
  const surface = window.location.pathname.startsWith("/ops") ? "ops" : window.location.pathname.startsWith("/business/") ? "agency" : "site";
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts, headers: { ...base, "X-Surface": surface, ...(opts.headers || {}) } });
  const onLogin = /^\/(auth|ops\/login)/.test(window.location.pathname);
  const reason = r.status === 401 ? r.headers.get("X-Auth-Reason") : null;
  if ((reason === "superseded" || reason === "deactivated") && !kicked && !onLogin) {
    kicked = true;
    sessionStorage.setItem(NOTICE_KEY, reason === "deactivated" ? "This account has been deactivated. Contact support@vistolane.com if you believe this is a mistake." : "Signed in elsewhere. Only one active session is allowed per account, so this device was signed out. Sign in again to continue here.");
    window.location.assign(window.location.pathname.startsWith("/ops") ? "/ops/login" : "/auth");
  }
  return r;
};
export const takeAuthNotice = () => { const n = sessionStorage.getItem(NOTICE_KEY); if (n) setTimeout(() => sessionStorage.removeItem(NOTICE_KEY), 4000); return n; };

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

async function call(path, body, method = "POST") {
  const r = await apiFetch(path, { method, body: body ? JSON.stringify(body) : undefined });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiErrorDetail(data.detail));
  return data;
}

// The backend sets a readable `vl_auth=1` cookie next to the HttpOnly session; without it, public pages skip the /me probe (and its 401).
const PRIVATE_PATH = /^\/(portal|account|business|ops|marketing|auth|register|reset|join|plan|embed|groups|start)(\/|$)/;
const hasAuthHint = () => /(^|;\s*)vl_auth=1(;|$)/.test(document.cookie);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const refresh = useCallback(async ({ initial = false } = {}) => {
    if (initial && !hasAuthHint() && !PRIVATE_PATH.test(window.location.pathname)) { setUser(false); return; }
    try { const r = await apiFetch("/api/auth/me"); setUser(r.ok ? await r.json() : false); } catch { setUser(false); }
  }, []);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setUser(false); return; }
    refresh({ initial: true });
  }, [refresh]);
  const value = {
    user,
    login: async (email, password) => { const u = await call("/api/auth/login", { email, password }); setUser(u); return u; },
    register: async (body) => { const u = await call("/api/auth/register", body); setUser(u); return u; },
    registerPartner: async (body) => { const u = await call("/api/auth/register-partner", body); setUser(u); return u; },
    updateProfile: async (body) => { const u = await call("/api/auth/profile", body, "PATCH"); setUser(u); return u; },
    logout: async () => { await call("/api/auth/logout", {}); setUser(false); },
    googleSignIn: (returnTo) => {
      if (returnTo) sessionStorage.setItem("vl-auth-return", returnTo);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/portal";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    },
    phoneStart: (phone, channel = "sms") => call("/api/auth/phone/start", { phone, channel }),
    phoneLogin: async (phone, code, name = "") => { const u = await call("/api/auth/phone/login", { phone, code, name }); setUser(u); return u; },
    setUser,
    refresh,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try { const u = await call("/api/auth/google/session", { session_id: sid }); setUser(u); window.history.replaceState(null, "", location.pathname); const back = sessionStorage.getItem("vl-auth-return"); sessionStorage.removeItem("vl-auth-return"); navigate(back && back.startsWith("/") ? back : "/portal", { replace: true, state: { user: u } }); }
      catch { setUser(false); navigate("/auth?error=google", { replace: true }); }
    })();
  }, [location, navigate, setUser]);
  return <div className="grid min-h-[50vh] place-items-center text-sm text-label2" data-testid="auth-callback">Signing you in…</div>;
}
